import { Team, TeamInvitation, TeamInvitationStatus, TeamMember } from '../../domain/models';
import { TeamRepository } from '../../domain/repositories';
import { TeamDataSource } from '../datasources/TeamDataSource';
import { teamEntityToModel, teamModelToEntity } from '../entities/TeamEntity';
import { teamMemberEntityToModel } from '../entities/TeamMemberEntity';
import { teamInvitationEntityToModel } from '../entities/TeamInvitationEntity';

export class TeamRepositoryImpl implements TeamRepository {
  constructor(private dataSource: TeamDataSource) {}

  async create(team: Omit<Team, 'id' | 'createdAt' | 'updatedAt'>): Promise<Team> {
    const entity = teamModelToEntity(team);
    const createdEntity = await this.dataSource.create(entity as any);
    return teamEntityToModel(createdEntity);
  }

  async getById(id: string): Promise<Team | null> {
    const entity = await this.dataSource.getById(id);
    if (!entity) return null;
    return teamEntityToModel(entity);
  }

  async getUserTeams(userId: string): Promise<Team[]> {
    const entities = await this.dataSource.getUserTeams(userId);
    return entities.map(teamEntityToModel);
  }

  async getPublicTeams(limit: number, offset: number): Promise<Team[]> {
    const entities = await this.dataSource.getPublicTeams(limit, offset);
    return entities.map(teamEntityToModel);
  }

  async update(id: string, team: Partial<Team>): Promise<Team> {
    const entity = teamModelToEntity(team);
    const updatedEntity = await this.dataSource.update(id, entity);
    return teamEntityToModel(updatedEntity);
  }

  async delete(id: string): Promise<void> {
    await this.dataSource.delete(id);
  }

  async countOwnedTeams(userId: string): Promise<number> {
    return this.dataSource.countOwnedTeams(userId);
  }

  async addMember(teamId: string, userId: string): Promise<TeamMember> {
    const entity = await this.dataSource.addMember(teamId, userId);
    return teamMemberEntityToModel(entity);
  }

  async getMembers(teamId: string): Promise<TeamMember[]> {
    const entities = await this.dataSource.getMembers(teamId);
    return entities.map(teamMemberEntityToModel);
  }

  async removeMember(teamId: string, userId: string): Promise<void> {
    await this.dataSource.removeMember(teamId, userId);
  }

  async sendInvitation(
    teamId: string,
    inviterId: string,
    inviteeId: string,
    message?: string,
  ): Promise<TeamInvitation> {
    const entity = await this.dataSource.sendInvitation(teamId, inviterId, inviteeId, message);
    return teamInvitationEntityToModel(entity);
  }

  async getInvitationsReceived(userId: string): Promise<TeamInvitation[]> {
    const entities = await this.dataSource.getInvitationsReceived(userId);
    return entities.map(teamInvitationEntityToModel);
  }

  async getInvitationsSent(userId: string): Promise<TeamInvitation[]> {
    const entities = await this.dataSource.getInvitationsSent(userId);
    return entities.map(teamInvitationEntityToModel);
  }

  async updateInvitationStatus(invitationId: string, status: TeamInvitationStatus): Promise<void> {
    await this.dataSource.updateInvitationStatus(invitationId, status);
  }
}
